import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { NotificationBadge, NotificationPanel } from "@/modules/notifications/components";
import {
  useNotifications,
  useUnreadCount,
  useNotificationSocket
} from "@/modules/notifications/hooks";

// Painel: w-96 (384px) e maxHeight 600 — devem casar com NotificationPanel.
const PANEL_WIDTH = 384;
const PANEL_MAX_HEIGHT = 600;

interface NotificationsDropdownProps {
  /** 'sm' (default) = rodapé da sidebar; 'md' = appbar mobile (botão 38px). */
  size?: 'sm' | 'md';
}

export const NotificationsDropdown: React.FC<NotificationsDropdownProps> = ({ size = 'sm' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{ left: number; top: number }>({ left: 0, top: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Fetch notifications and unread count
  const { data: notificationsData } = useNotifications({ limit: 10, read: false });
  const { data: unreadCount = 0 } = useUnreadCount();

  // Setup real-time notifications via Socket.IO
  useNotificationSocket();

  // Posiciona o painel a partir do trigger (rodapé inferior-esquerdo da sidebar):
  // abre à DIREITA do ícone (para fora da sidebar) e para CIMA (base do painel junto ao trigger).
  const computePosition = useCallback(() => {
    const triggerEl = triggerRef.current;
    if (!triggerEl) return;
    const triggerRect = triggerEl.getBoundingClientRect();

    // Altura real do painel quando já montado; senão usa o teto (maxHeight),
    // sempre limitada à viewport.
    const measured = panelRef.current?.offsetHeight;
    const panelHeight = Math.min(
      measured && measured > 0 ? measured : PANEL_MAX_HEIGHT,
      PANEL_MAX_HEIGHT,
      window.innerHeight - 16
    );

    // Flyout à direita da SIDEBAR (não do ícone) quando o trigger vive nela — estilo submenu;
    // senão à direita do ícone (ex.: appbar mobile). clamp à viewport.
    const sidebarRoot = triggerEl.closest('[data-sidebar-root]');
    let left = (sidebarRoot ? sidebarRoot.getBoundingClientRect().right : triggerRect.right) + 8;
    left = Math.min(left, window.innerWidth - PANEL_WIDTH - 8);
    left = Math.max(8, left);

    // Abre para BAIXO, alinhado ao topo do trigger (flyout lateral estilo submenu);
    // clamp à viewport para não vazar embaixo.
    let top = triggerRect.top;
    top = Math.max(8, Math.min(top, window.innerHeight - panelHeight - 8));

    setPosition({ left, top });
  }, []);

  // Recalcula ao abrir (após o painel montar, para medir a altura real) e em resize/scroll.
  useLayoutEffect(() => {
    if (!isOpen) return;
    computePosition();
    window.addEventListener('resize', computePosition);
    window.addEventListener('scroll', computePosition, true);
    return () => {
      window.removeEventListener('resize', computePosition);
      window.removeEventListener('scroll', computePosition, true);
    };
  }, [isOpen, computePosition]);

  // Clique-fora (portal-aware): fecha se o clique não está no trigger nem no painel.
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inTrigger = triggerRef.current?.contains(target) ?? false;
      const inPanel = panelRef.current?.contains(target) ?? false;
      if (!inTrigger && !inPanel) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const notifications = notificationsData?.notifications || [];

  return (
    <>
      <NotificationBadge
        ref={triggerRef}
        count={unreadCount}
        onClick={() => setIsOpen(!isOpen)}
        isActive={isOpen}
        size={size}
      />

      {isOpen && (
        <NotificationPanel
          ref={panelRef}
          notifications={notifications}
          unreadCount={unreadCount}
          position={position}
          panelWidth={PANEL_WIDTH}
          onClose={() => setIsOpen(false)}
        />
      )}
    </>
  );
};
